import { onMounted, onUnmounted, type Ref, watch } from 'vue'
import { useRouter } from 'vue-router'

const HISTORY_STATE_KEY = 'gameFullscreen'

const ROUTER_OWNED_STATE_KEYS = ['back', 'current', 'forward', 'replaced', 'position', 'scroll']

function appOwnedState() {
    const state = { ...(window.history.state as Record<string, unknown> | null) }
    for (const key of ROUTER_OWNED_STATE_KEYS) {
        delete state[key]
    }

    return state
}

type Options = {
    isFullscreen: Ref<boolean>
    isEnabled: () => boolean
    open: () => void
    close: () => void
}

export function useFullscreenBackButton({ isFullscreen, isEnabled, open, close }: Options) {
    const router = useRouter()

    let sentinelPosition: number | null = null

    function isSentinelActive() {
        return !!(window.history.state as Record<string, unknown> | null)?.[HISTORY_STATE_KEY]
    }

    function currentPosition() {
        return (window.history.state as { position?: number } | null)?.position ?? null
    }

    // Only our own pair of entries may move fullscreen: the sentinel reopens it, the entry right below
    // closes it. Anything else is somebody else's — AdSense pushes a hash entry during ad breaks and that
    // does fire popstate — and must be ignored, or the ad would tear the game down.
    function onPopState() {
        if (!isEnabled()) {
            return
        }

        const position = currentPosition()
        if (sentinelPosition === null) {
            return
        }

        if (position === sentinelPosition && isSentinelActive()) {
            open()
        } else if (position === sentinelPosition - 1 && isFullscreen.value) {
            close()
        }
    }

    watch(isFullscreen, (value) => {
        if (value) {
            if (isEnabled()) {
                if (!isSentinelActive()) {
                    router.options.history.push(router.options.history.location, {
                        ...appOwnedState(),
                        [HISTORY_STATE_KEY]: true,
                    })
                }
                sentinelPosition = currentPosition()
            }
            return
        }

        const position = currentPosition()
        if (sentinelPosition === null || position === sentinelPosition - 1) {
            // back already took us off the sentinel; keep the pair so forward can still reopen
            return
        }

        // the key, not the position: a foreign entry inherits the position of the one it was pushed from,
        // so an ad breaks hash entry reports the sentinels position too
        if (position === sentinelPosition && isSentinelActive()) {
            // AdSense stubs window.history.back during ad breaks; triggerListeners=false avoids a phantom navigation
            router.options.history.go(-1, false)
            return
        }

        sentinelPosition = null
    })

    onMounted(() => {
        window.addEventListener('popstate', onPopState)

        if (isSentinelActive()) {
            router.options.history.replace(router.options.history.location, { [HISTORY_STATE_KEY]: false })
            router.options.history.go(-1, false)
        }
    })
    onUnmounted(() => window.removeEventListener('popstate', onPopState))
}
